import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExistingCraftRecord } from "../../models/craft-record";
import { ExistingTask } from "../../models/task";
import { ExistingStiltInvoice, StiltInvoiceStatus } from "../../models/invoice";
import { ExistingEstimate } from "../../models/estimate";
import { TaskStatus } from "../../models/task-status";
import { convertToReadableTimestampDate } from "../../assets/js/convertToReadableTimestamp";
import { DollarCurrency } from "../../currency";
import { Link } from "react-router-dom";
import * as urls from "../../urls";
import { PhotoForGallery } from "./ProjectPhotoGallery";
import BaseButtonTertiary from "../../components/BaseButtonTertiary";

interface CraftRecordListProps {
  craftRecords: ExistingCraftRecord[];
  tasksByCraftRecord: Record<string, ExistingTask[]>;
  invoicesByCraftRecord: Record<string, ExistingStiltInvoice[]>;
  estimatesByCraftRecord: Record<string, ExistingEstimate[]>;
  photosByCraftRecord: Record<string, PhotoForGallery[]>;
  userDisplayNamesMap: Record<string, string>;
  isUserDisplayNamesLoading: boolean;
  siteKey: string;
}

export default function CraftRecordList({
  craftRecords,
  tasksByCraftRecord,
  invoicesByCraftRecord,
  estimatesByCraftRecord,
  photosByCraftRecord,
  siteKey,
}: CraftRecordListProps) {
  const [expandedJobs, setExpandedJobs] = useState<Record<string, boolean>>({});
  const navigate = useNavigate();

  // Toggle job expansion
  const toggleJobExpand = (jobId: string) => {
    setExpandedJobs((prev) => ({
      ...prev,
      [jobId]: !prev[jobId],
    }));
  };

  const goToJobPage = (jobId: string) => {
    navigate(`${urls.WORK_RECORD_AND_TASKS_URL}/${jobId}`);
  };

  if (craftRecords.length === 0) {
    return (
      <div className="rounded-md bg-gray-50 py-8 text-center">
        <p className="text-gray-500">No jobs found for this project.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {craftRecords.map((job) => {
        const jobId = job.id.split("/").pop() || job.id;
        const tasks = tasksByCraftRecord[jobId] || [];
        const invoices = invoicesByCraftRecord[jobId] || [];
        const estimates = estimatesByCraftRecord[jobId] || [];
        const photos = photosByCraftRecord[jobId] || [];

        const completedTasks = tasks.filter(
          (task) => task.taskStatus === TaskStatus.COMPLETE,
        ).length;

        return (
          <div
            key={job.id}
            className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow"
          >
            <div
              className="flex cursor-pointer items-center justify-between p-4"
              onClick={() => toggleJobExpand(jobId)}
            >
              <div className="flex items-center gap-2">
                <button className="text-gray-400 hover:text-gray-600 focus:outline-none">
                  {expandedJobs[jobId] ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4.001 4a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>
                <div>
                  <h3 className="flex items-center font-medium text-gray-900">
                    <Link
                      to={`${urls.WORK_RECORD_AND_TASKS_URL}/${jobId}`}
                      className="hover:underline"
                    >
                      {job.title}
                    </Link>
                    {job.open ? (
                      <span className="ml-2 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                        Open
                      </span>
                    ) : (
                      <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800">
                        Closed
                      </span>
                    )}
                  </h3>
                  <p className="text-sm text-gray-500">
                    Created:{" "}
                    {convertToReadableTimestampDate(job.timestampRecordCreated)}
                  </p>
                </div>
              </div>
              <div className="text-right text-sm text-gray-500">
                <div>
                  Tasks: {completedTasks}/{tasks.length}
                </div>
                <div>Invoices: {invoices.length}</div>
              </div>
              <div className="flex items-center gap-2">
                <BaseButtonTertiary
                  className="text-sm"
                  onClick={() => goToJobPage(jobId)}
                >
                  View Job Page
                </BaseButtonTertiary>
              </div>
            </div>

            {expandedJobs[jobId] && (
              <div className="border-t border-gray-200">
                {/* Job Description Section */}
                {job.description && (
                  <div className="border-b border-gray-200 p-4">
                    <h4 className="mb-2 font-medium text-gray-700">
                      Description
                    </h4>
                    <p className="text-sm text-gray-600">{job.description}</p>
                  </div>
                )}

                {/* Tasks Section */}
                <div className="border-b border-gray-200 p-4">
                  <h4 className="mb-2 font-medium text-gray-700">Tasks</h4>
                  {tasks.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      No tasks for this job
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th
                              scope="col"
                              className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                            >
                              Title
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                            >
                              Status
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                            >
                              Created
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {tasks.map((task) => (
                            <tr key={task.id} className="hover:bg-gray-50">
                              <td className="whitespace-nowrap px-3 py-2 text-sm font-medium text-gray-900">
                                <Link
                                  to={`${urls.WORK_RECORD_AND_TASKS_URL}/${jobId}/${task.id.split("/").pop()}`}
                                  className="hover:underline"
                                >
                                  {task.title}
                                </Link>
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 text-sm">
                                {task.taskStatus === TaskStatus.COMPLETE ? (
                                  <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                                    Completed
                                  </span>
                                ) : (
                                  <span className="rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                                    In Progress
                                  </span>
                                )}
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">
                                {convertToReadableTimestampDate(
                                  task.timestampCreated,
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Invoices Section */}
                <div className="border-b border-gray-200 p-4">
                  <h4 className="mb-2 font-medium text-gray-700">Invoices</h4>
                  {invoices.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      No invoices for this job
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th
                              scope="col"
                              className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                            >
                              Invoice #
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                            >
                              Status
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                            >
                              Amount
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                            >
                              Due
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {invoices.map((invoice) => (
                            <tr key={invoice.id} className="hover:bg-gray-50">
                              <td className="whitespace-nowrap px-3 py-2 text-sm font-medium text-gray-900">
                                <Link
                                  to={`${urls.INVOICES_URL}/${invoice.id}`}
                                  className="hover:underline"
                                >
                                  #{invoice.invoiceNumber || ""}
                                </Link>
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 text-sm">
                                {invoice.status === StiltInvoiceStatus.PAID ? (
                                  <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                                    Paid
                                  </span>
                                ) : invoice.status ===
                                  StiltInvoiceStatus.PENDING ? (
                                  <span className="rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                                    Pending
                                  </span>
                                ) : (
                                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800">
                                    {invoice.status}
                                  </span>
                                )}
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-900">
                                {DollarCurrency.format(invoice.totalAmount)}
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-900">
                                {DollarCurrency.format(invoice.amountDue)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Estimates Section */}
                <div className="border-b border-gray-200 p-4">
                  <h4 className="mb-2 font-medium text-gray-700">Estimates</h4>
                  {estimates.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      No estimates for this job
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th
                              scope="col"
                              className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                            >
                              Estimate #
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                            >
                              Status
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                            >
                              Created
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {estimates.map((estimate) => (
                            <tr key={estimate.id} className="hover:bg-gray-50">
                              <td className="whitespace-nowrap px-3 py-2 text-sm font-medium text-gray-900">
                                <Link
                                  to={`${urls.CUSTOMERS_URL}${urls.ESTIMATES_URL_AUTH}/${estimate.id}`}
                                  className="hover:underline"
                                >
                                  #{estimate.estimateNumber || ""}
                                </Link>
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 text-sm">
                                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800">
                                  {estimate.status}
                                </span>
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">
                                {convertToReadableTimestampDate(
                                  estimate.timestampCreated,
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Photos Section */}
                {photos.length > 0 && (
                  <div className="p-4">
                    <h4 className="mb-2 font-medium text-gray-700">
                      Photos ({photos.length})
                    </h4>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                      {photos.slice(0, 8).map((photo) => (
                        <a
                          key={photo.id}
                          href={photo.photoURL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="aspect-square overflow-hidden rounded border border-gray-200"
                        >
                          <img
                            src={photo.photoURLReduced || photo.photoURL}
                            alt="Job"
                            className="h-full w-full object-cover"
                          />
                        </a>
                      ))}
                      {photos.length > 8 && (
                        <div className="flex aspect-square items-center justify-center rounded border border-gray-200 bg-gray-100 text-gray-500">
                          +{photos.length - 8} more
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
